import { UseFormMethods } from "react-hook-form";
import { LessonFormData } from "../types";
import { LOCALES } from "../constants";
import { readLocale, walkTranslatableFields } from "../utils";

/**
 * Watches form state and returns a list of human-readable issues that should
 * block saving. Returning an empty array means the form is submittable.
 *
 * Two categories are enforced here:
 *   1. Every question must have at least one answer marked correct.
 *   2. Every LocalizedText with source content must also have non-empty
 *      values in every other locale — the backend round-trip assumes one
 *      translation row per locale, and partial translations lead to subtle
 *      rendering bugs in the patient app.
 */
export const useLessonValidation = (form: UseFormMethods<LessonFormData>): string[] => {
  const parts = form.watch("parts") ?? [];
  const issues: string[] = [];

  // At-least-one-correct-answer check — not a translation concern, so walk questions directly.
  parts.forEach((part, pi) => {
    (part.questions ?? []).forEach((q, qi) => {
      const answers = q.answers ?? [];
      if (answers.length > 0 && !answers.some(a => a.correct)) {
        issues.push(`Part ${pi + 1} — Question ${qi + 1}: mark at least one answer as correct`);
      }
    });
  });

  for (const { label, value } of walkTranslatableFields(parts)) {
    const hasSource = LOCALES.some(l => readLocale(value, l.code).trim());
    if (!hasSource) continue;
    const missing = LOCALES.filter(l => !readLocale(value, l.code).trim()).map(l => l.label);
    if (missing.length) issues.push(`${label}: missing translations (${missing.join(", ")})`);
  }

  return issues;
};
